import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

// ✅ wspólna funkcja – tak samo jak w tabeli, uwzględnia wartości ujemne
const getSalesValue = (row) => {
  const raw =
    row.K_15 ??
    row.K_19 ??
    row.K_10 ??
    row.K_21 ??
    row.K_17 ??
    row.K_11 ??
    row.K_22 ??
    0;

  const value =
    typeof raw === "string"
      ? parseFloat(raw.replace(",", "."))
      : Number(raw);

  return Number.isFinite(value) ? value : 0; // może być ujemne
};

const MonthlySalesByYearChart = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  // ✅ już bez Math.max(0, ...) – korekty wchodzą normalnie
  const obliczSprzedaz = (row) => getSalesValue(row);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const years = Array.from(
      new Set(data.map((r) => parseInt(r.Rok, 10)).filter((y) => !isNaN(y)))
    ).sort((a, b) => a - b);

    setAvailableYears(years);
    if (!selectedYear && years.length > 0) {
      setSelectedYear(Math.max(...years));
    }
  }, [data]); // eslint-disable-line

  const { chartData } = useMemo(() => {
    if (!selectedYear || !data) return { chartData: null };

    // agregacja po miesiącach dla danego roku
    const aggYear = (year) => {
      const sums = Array(12).fill(0);
      data.forEach((row) => {
        const y = parseInt(row.Rok, 10);
        if (y === year) {
          const d = new Date(row.DataWystawienia);
          if (!isNaN(d)) {
            const m = d.getMonth(); // 0..11
            sums[m] += obliczSprzedaz(row);
          }
        }
      });
      return sums;
    };

    const current = aggYear(selectedYear);
    const previous = aggYear(selectedYear - 1);

    const GREEN = "#2e7d32";
    const ORANGE = "#fb8c00";

    const labels = [
      "I","II","III","IV","V","VI",
      "VII","VIII","IX","X","XI","XII"
    ];

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: `Sprzedaż ${selectedYear}`,
            data: current,
            backgroundColor: "rgba(46,125,50,0.70)", // zielony (bieżący rok)
            borderColor: GREEN,
            borderWidth: 1,
          },
          {
            label: `Sprzedaż ${selectedYear - 1}`,
            data: previous,
            backgroundColor: "rgba(251,140,0,0.55)", // pomarańczowy (poprzedni rok)
            borderColor: ORANGE,
            borderWidth: 1,
          },
        ],
      },
    };
  }, [selectedYear, data]);

  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="monthly-sales-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="monthly-sales-tooltip"
        place="left"
        content={
          <>
            <strong>Sprzedaż według miesięcy</strong>
            <br />
            Zestawienie wartości sprzedaży w miesiącach:
            <br />
            {selectedYear} vs {selectedYear - 1}.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>{" "}
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: false },
            y: {
              beginAtZero: true,
              title: { display: true, text: "Sprzedaż" },
              ticks: {
                callback: (value) =>
                  Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " "),
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: "Sprzedaż według miesięcy",
            },
            legend: { display: true },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const label = ctx.dataset.label || "";
                  const val = ctx.parsed.y || 0;
                  const formatted = Number(val)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                  return `${label}: ${formatted}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default MonthlySalesByYearChart;
